import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const PlaybackCoordinatorContext = createContext(null);

export const usePlaybackCoordinator = () => {
  const context = useContext(PlaybackCoordinatorContext);
  if (!context) {
    throw new Error('usePlaybackCoordinator must be used within PlaybackCoordinatorProvider');
  }
  return context;
};

export const PlaybackCoordinatorProvider = ({ children }) => {
  // Ref for synchronous checks (no stale closure issues)
  const activeOwnerRef = useRef('none');
  // State for reactive rendering
  const [activeOwner, setActiveOwner] = useState('none');
  // Registered stop functions from each system
  const stopFnsRef = useRef({ card: null, preview: null });

  const registerStopFn = useCallback((system, fn) => {
    stopFnsRef.current[system] = fn;
  }, []);

  // Request ownership — stops the other system synchronously first
  const requestOwnership = useCallback((system) => {
    const current = activeOwnerRef.current;
    if (current !== 'none' && current !== system) {
      const stopFn = stopFnsRef.current[current];
      if (stopFn) stopFn();
    }
    activeOwnerRef.current = system;
    setActiveOwner(system);
  }, []);

  // Release ownership — only if caller is the current owner
  const releaseOwnership = useCallback((system) => {
    if (activeOwnerRef.current === system) {
      activeOwnerRef.current = 'none';
      setActiveOwner('none');
    }
  }, []);

  return (
    <PlaybackCoordinatorContext.Provider value={{
      activeOwner,
      requestOwnership,
      releaseOwnership,
      registerStopFn
    }}>
      {children}
    </PlaybackCoordinatorContext.Provider>
  );
};
